import React, { Fragment, useCallback, useEffect } from 'react';
import { Stack, Text, Divider, Checkbox, ControlLink, Popover } from '@moda/om';
import { useParams, useHistory } from 'react-router-dom';
import WarningIcon from '@moda/icons/warning-16';
import { omit } from 'ramda';
import { throwQueryError } from '../../../../lib/apollo/throwQueryError';
import { useUrlParams } from '../../../../hooks/useUrlParams';
import { useAffirmSecondPayment } from '../../../../hooks/useAffirmSecondPayment';
import { useUpdateOrder, OrderState } from '../../../../hooks/useUpdateOrder';
import { OrderItem } from '../../../../hooks/useOrder';
import { AccountOrderItem } from '../../../OrderItem/AccountOrderItem';
import { LoadingBalls } from '../../../LoadingBalls';
import { AccountOrderDetailPageState, PaymentSource } from '../../../../constants';
import { useOrderDetails } from '../../../OrderDetails';
import { Box } from '../../../Box';
import { OrderDetailsItemsSectionProps } from '../orderDetailsItemsSectionsConfig';

import './OrderDetailsItemsSection.scss';

export const OrderDetailsItemsSection: React.FC<OrderDetailsItemsSectionProps> = ({
  title,
  propertyName,
  alternateTitle,
  hasPaymentOption
}) => {
  const { orderSummary, orderItems } = useOrderDetails();

  const { state: orderDetailPageState } = useParams<{
    state?: AccountOrderDetailPageState;
  }>();
  const history = useHistory();
  const { getHrefFor, params } = useUrlParams();
  const { isAffirmQueryParamPresent, isAffirmAvailable } = useAffirmSecondPayment();

  const orderState = orderSummary?.attributes.orderState;
  const selectedItemsIds = orderSummary?.attributes.selectedItemsIds;
  const hasPendingAuthorizationItems = orderSummary?.attributes.hasPendingAuthorizationItems;
  const remainingBalance = orderSummary?.attributes.paymentSummary.remainingBalance.price;
  const affirmSecondChargeEnabled = orderSummary?.attributes.affirmSecondChargeEnabled;

  const componentTitle = orderState === OrderState.Default ? title : alternateTitle;

  const {
    updateOrderSelectedItems,
    setOrderStateToDefault,
    setOrderStateToPaying,
    setOrderPayRemainingBalance,
    error,
    loading
  } = useUpdateOrder(orderSummary?.id || '');

  if (error) throwQueryError(error);

  const itemIds = orderSummary?.relationships?.[propertyName]?.data.map(({ id }) => id);

  const items = itemIds?.map(itemId => orderItems?.find(({ id }) => id === itemId));

  useEffect(() => {
    if (
      hasPaymentOption &&
      orderDetailPageState === AccountOrderDetailPageState.Paying &&
      orderState === OrderState.Default
    ) {
      setOrderStateToPaying();
    }
  }, [hasPaymentOption, orderDetailPageState, orderState, setOrderStateToPaying]);

  useEffect(() => {
    if (isAffirmQueryParamPresent && isAffirmAvailable) {
      setOrderPayRemainingBalance(PaymentSource.Affirm.toLowerCase());
    }
  }, [isAffirmQueryParamPresent, isAffirmAvailable, setOrderPayRemainingBalance]);

  const handleModeChange = useCallback(() => {
    if (orderState === OrderState.Default) {
      setOrderStateToPaying();

      return;
    }

    setOrderStateToDefault();
    // After setting the state to default, reset the parameter to its default state (absent)
    if (orderDetailPageState) {
      history.replace(getHrefFor(omit(['state'], params)));
    }
  }, [
    history,
    orderDetailPageState,
    orderState,
    params,
    setOrderStateToDefault,
    setOrderStateToPaying,
    getHrefFor
  ]);

  const handleItemCheck = useCallback(
    (item: OrderItem) => {
      const { id } = item;

      if (!selectedItemsIds) return;

      if (selectedItemsIds.includes(Number(id))) {
        updateOrderSelectedItems(
          selectedItemsIds.filter(selectedItemId => selectedItemId !== Number(id))
        );

        return;
      }

      updateOrderSelectedItems([...selectedItemsIds, Number(id)]);
    },
    [selectedItemsIds, updateOrderSelectedItems]
  );

  useEffect(() => {
    if (
      orderDetailPageState === AccountOrderDetailPageState.Paying &&
      hasPendingAuthorizationItems
    ) {
      setOrderStateToDefault();
    }
  }, [hasPendingAuthorizationItems, orderDetailPageState, setOrderStateToDefault]);

  if (!orderSummary || !orderSummary.relationships) return null;

  if (!items || !items.length) return null;

  if (loading) {
    return (
      <Stack direction="horizontal" justifyContent="center" space={0}>
        <LoadingBalls />
      </Stack>
    );
  }

  return (
    <Stack className="OrderDetailsItemsSection" space={3}>
      <Stack direction="horizontal" alignItems="center" justifyContent="space-between" space={4}>
        <Text family="serif" treatment="h5">
          {affirmSecondChargeEnabled ? 'A payment is due for the selected items' : componentTitle}
        </Text>

        {hasPaymentOption && (
          <Stack direction="horizontal" space={2}>
            <ControlLink
              disabled={hasPendingAuthorizationItems}
              underlined={false}
              onClick={handleModeChange}
            >
              <>
                {orderState === OrderState.Default &&
                  remainingBalance !== '0.0' &&
                  'Pay remaining balance'}
                {orderState === OrderState.PayRemainingBalance && 'Cancel'}
              </>
            </ControlLink>

            {hasPendingAuthorizationItems && (
              <Popover
                anchor="topRight"
                content={
                  <Stack className="OrderDetailsItemsSection__popover-content" space={0}>
                    <Text treatment="body1">You have a payment that's pending authorization.</Text>
                    <Text treatment="body2">
                      You must wait for it to finish before paying the remaining balance.
                    </Text>
                  </Stack>
                }
              >
                <WarningIcon />
              </Popover>
            )}
          </Stack>
        )}
      </Stack>

      <Box>
        {items.map(
          (item, index: number) =>
            item && (
              <Fragment key={item.id || index}>
                {propertyName === 'shippedItems' && (
                  <AccountOrderItem item={item} showExtraDetails showShippingStatus={false} />
                )}
                {propertyName !== 'shippedItems' && (
                  <Stack
                    space={3}
                    direction="horizontal"
                    className="OrderDetailsItemsSection__item"
                  >
                    {(orderState === OrderState.PayRemainingBalance ||
                      orderState === OrderState.Failure) &&
                      item.attributes.prices.totalBalancePayment.price > 0 && (
                        <Checkbox
                          className="OrderDetailsItemsSection__checkbox"
                          checked={Boolean(selectedItemsIds?.find(id => id.toString() === item.id))}
                          onClick={() => handleItemCheck(item)}
                          disabled={Boolean(affirmSecondChargeEnabled)}
                        />
                      )}

                    <AccountOrderItem item={item} showExtraDetails showShippingStatus={false} />
                  </Stack>
                )}
                {index < items.length - 1 && <Divider />}
              </Fragment>
            )
        )}
      </Box>
    </Stack>
  );
};
